import { FC, useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import Cart from '../../Cart';
import Status from '../../../../Status/Status';
import styles from '../../ProductPage.module.scss';
import Settings from '../../../../Icons/Settings';
import { Link, useNavigate } from 'react-router-dom';
import ModalAddToCart from '../../../../ModalAddToCart/ModalAddToCart';
import { useTypedSelector } from '../../../../../lib/hooks/useTypedSelector';
import Comment from '../../../../Icons/Comment';
import { generateImageUrl } from '../../../../../helpers/generateImageUrl';
import { getDocumentUrl } from '../../../../../api/document/getDocumentUrl';
import { ImageResponseType } from '../../../../../types/base';
import Arrow from '../../../../Icons/Arrow';
import ModalComment from '../../../../ModalComment/ModalComment';

import { RepInstruction as RepInstructionType } from '../../../../../types/products';
import { formatBits } from '../../../../../helpers/formatBits';

export interface IItemProps {
  id: string;
  name: string;
  article_number: string;
  cmmf: string;
  type: string;
  status?: string;
  commercial_number: string;
  image?: ImageResponseType;
  explodedViews?: RepInstructionType[];
  repInstruction?: RepInstructionType[];
  electricalScheme?: RepInstructionType[];
  techInfo?: RepInstructionType[];
  className?: string;
  replacedBy?: any[];
  slug: string;
  metaData?: {
    count: any;
  };
}

export interface IListProps {
  list: any[];
  view?: string;
  type?: string;
}

export const ItemCard: FC<IItemProps> = ({
  name,
  article_number,
  commercial_number,
  explodedViews,
  repInstruction = [],
  electricalScheme = [],
  techInfo = [],
  type,
  image,
  id,
  slug,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [electricalSchemeLinks, setElectricalSchemeLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [repInstructionLinks, setRepInstructionLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [techInfoLinks, setTechInfoLinks] = useState<
    { url: string; size: number }[]
  >([]);

  useEffect(() => {
    if (electricalScheme.length) {
      electricalScheme.forEach((item) => {
        if (!item.s3Bucket && !item.s3Key) return;
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setElectricalSchemeLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (repInstruction.length) {
      repInstruction.forEach((item) => {
        if (!item.s3Bucket && !item.s3Key) return;
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setRepInstructionLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (techInfo.length) {
      techInfo.forEach((item) => {
        if (!item.s3Bucket && !item.s3Key) return;
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setTechInfoLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }
  }, [electricalScheme, repInstruction, techInfo]);

  return (
    <Link
      to={'/product/' + slug}
      state={{ type: type }}
      className={styles.ItemCard}
    >
      <div className={styles.image}>
        <img
          src={
            image?.s3Key
              ? generateImageUrl(image?.s3Key)
              : '/images/placeholderImage.png'
          }
          alt="image"
        />
      </div>
      <div className={styles.info}>
        <div className={styles.name}>{name}</div>
        <span>Артикул: {article_number}</span>
        <span>Коммерческий код: {commercial_number}</span>
      </div>
      <div className={styles.documents}>
        <div
          className={clsx(styles.select, { [styles.open]: isOpen })}
          ref={modalRef}
        >
          {electricalSchemeLinks.length ||
          repInstructionLinks.length ||
          techInfoLinks.length ? (
            <>
              <span
                className={styles.button}
                onClick={(e) => {
                  e.preventDefault();
                  isOpen ? setIsOpen(false) : setIsOpen(true);
                }}
              >
                <Settings />
              </span>
              <div className={styles.options}>
                <ul>
                  {electricalSchemeLinks.map((item) => (
                    <li key={item.url}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.url, '_blank');
                        }}
                      >
                        Взрыв-схема.pdf <span>({formatBits(item.size)})</span>
                      </button>
                    </li>
                  ))}
                  {repInstructionLinks.map((item) => (
                    <li key={item.url}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.url, '_blank');
                        }}
                      >
                        Руководство по ремонту.pdf{' '}
                        <span>({formatBits(item.size)})</span>
                      </button>
                    </li>
                  ))}
                  {techInfoLinks.map((item) => (
                    <li key={item.url}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.url, '_blank');
                        }}
                      >
                        Tехническая информация.pdf{' '}
                        <span>({formatBits(item.size)})</span>
                      </button>
                    </li>
                  ))}
                  {explodedViews &&
                    explodedViews.length &&
                    explodedViews[0].s3Bucket &&
                    explodedViews[0].s3Key && (
                      <a
                        className={styles.link_schema}
                        href={'/scheme/' + id}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open('/scheme/' + id);
                        }}
                      >
                        Интерактивная взрыв-схема
                        <Arrow />
                      </a>
                    )}
                </ul>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

export const Item: FC<IItemProps> = ({
  name,
  article_number,
  type,
  status,
  commercial_number,
  explodedViews,
  repInstruction,
  electricalScheme,
  techInfo,
  cmmf,
  id,
  image,
  className,
  replacedBy,
  metaData,
  slug,
}) => {
  const navigate = useNavigate();

  const { cart } = useTypedSelector((state) => state);
  const indexInCart = cart.findIndex((item) => item.product_id === id);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const [isModalActive, setModalActive] = useState(false);

  const handleModalOpen = () => {
    setModalActive(true);
    document.body.style.overflow = 'hidden';
  };
  const handleModalClose = () => {
    setModalActive(false);
    document.body.style.overflow = 'auto';
  };

  const [commentVisible, setCommentVisible] = useState(false);
  const [isModalCommentActive, setModalCommentActive] = useState(false);
  const handleModalCommentOpen = () => {
    setModalCommentActive(true);
  };
  const handleModalCommentClose = () => {
    setModalCommentActive(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [electricalSchemeLinks, setElectricalSchemeLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [repInstructionLinks, setRepInstructionLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [techInfoLinks, setTechInfoLinks] = useState<
    { url: string; size: number }[]
  >([]);

  useEffect(() => {
    if (electricalScheme && electricalScheme.length) {
      electricalScheme.forEach((item) => {
        if (!item.s3Bucket && !item.s3Key) return;
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setElectricalSchemeLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (repInstruction && repInstruction.length) {
      repInstruction.forEach((item) => {
        if (!item.s3Bucket && !item.s3Key) return;
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setRepInstructionLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (techInfo && techInfo.length) {
      techInfo.forEach((item) => {
        if (!item.s3Bucket && !item.s3Key) return;
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setTechInfoLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }
  }, [electricalScheme, repInstruction, techInfo]);

  let replacedByItem =
    replacedBy?.length && Array.isArray(replacedBy)
      ? replacedBy.filter(
          (i) =>
            i.displayData.status &&
            i.displayData.status !== 'discontinued' &&
            i.displayData.isActive,
        )[0] || null
      : null;

  const in_stoke = status !== 'discontinued';

  return (
    <>
      <Link
        to={'/product/' + slug}
        state={{ type: type }}
        className={clsx(styles.Item, className ? className : '')}
      >
        {type === 'model' ? (
          <>
            <span>{article_number}</span>
            <span>{commercial_number}</span>
            <span>{name}</span>
            <div>
              <div
                className={clsx(styles.select, { [styles.open]: isOpen })}
                ref={modalRef}
              >
                {electricalSchemeLinks.length ||
                repInstructionLinks.length ||
                techInfoLinks.length ? (
                  <>
                    <span
                      className={styles.button}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        isOpen ? setIsOpen(false) : setIsOpen(true);
                      }}
                    >
                      <Settings />
                    </span>
                    <div className={styles.options}>
                      <ul>
                        {electricalSchemeLinks.map((item) => (
                          <li key={item.url}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(item.url, '_blank');
                              }}
                            >
                              Взрыв-схема.pdf{' '}
                              <span>({formatBits(item.size)})</span>
                            </button>
                          </li>
                        ))}
                        {repInstructionLinks.map((item) => (
                          <li key={item.url}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(item.url, '_blank');
                              }}
                            >
                              Руководство по ремонту.pdf{' '}
                              <span>({formatBits(item.size)})</span>
                            </button>
                          </li>
                        ))}
                        {techInfoLinks.map((item) => (
                          <li key={item.url}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(item.url, '_blank');
                              }}
                            >
                              Tехническая информация.pdf{' '}
                              <span>({formatBits(item.size)})</span>
                            </button>
                          </li>
                        ))}
                        {explodedViews &&
                          explodedViews.length &&
                          explodedViews[0].s3Bucket &&
                          explodedViews[0].s3Key && (
                            <a
                              className={styles.link_schema}
                              href={'/scheme/' + id}
                              onClick={(e) => {
                                e.preventDefault();
                                window.open('/scheme/' + id);
                              }}
                            >
                              Интерактивная взрыв-схема
                              <Arrow />
                            </a>
                          )}
                      </ul>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.type}>
              <img
                src={
                  type === 'model'
                    ? '/icons/model.png'
                    : type === 'part'
                      ? '/icons/detail.png'
                      : '/icons/accessory.png'
                }
                alt={type}
              />
            </div>
            <span className={styles.article}>
              <img
                src={
                  type === 'model'
                    ? '/icons/model.png'
                    : type === 'part'
                      ? '/icons/detail.png'
                      : '/icons/accessory.png'
                }
                alt={type}
                className={styles.type_mob}
              />
              {article_number}
            </span>
            <span className={styles.name}>{name}</span>
            <div className={styles.status_holder}>
              {status && <Status status={status} list={true} />}
            </div>

            <div className={styles.button_holder}>
              {indexInCart === -1 ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleModalOpen();
                  }}
                  className={clsx({
                    [styles.no_stoke]:
                      !in_stoke && !replacedByItem ? true : false,
                  })}
                  disabled={!in_stoke && !replacedByItem ? true : false}
                >
                  <Cart />
                </button>
              ) : (
                <div className={styles.cart_holder}>
                  <button
                    className={clsx(styles.cart_btn)}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/cart');
                    }}
                  >
                    {cart[indexInCart].count} <Cart />
                  </button>
                  {cart[indexInCart].comment && (
                    <div
                      className={clsx(styles.comment, {
                        [styles.open]: commentVisible,
                      })}
                      onPointerEnter={() => setCommentVisible(true)}
                      onPointerLeave={() => setCommentVisible(false)}
                      onClick={(e) => {
                        (e.preventDefault(), handleModalCommentOpen());
                      }}
                    >
                      <Comment />
                      <p>{cart[indexInCart].comment}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </Link>
      {isModalCommentActive && (
        <ModalComment
          onClose={handleModalCommentClose}
          comment={cart[indexInCart].comment || ''}
        />
      )}
      {isModalActive && (
        <ModalAddToCart
          onClose={handleModalClose}
          data={{
            available:
              !metaData?.count && replacedByItem
                ? replacedByItem.metaData.count
                : metaData?.count,
            product_id:
              !metaData?.count && replacedByItem ? replacedByItem.id : id,
            name:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.name
                : name,
            status:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.status || ''
                : status || '',
            cmmf:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.cmmf
                : cmmf,
            article_number:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.partCode
                : article_number,
            type:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.type
                : type,
            image:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.image?.s3Key || ''
                : image?.s3Key || '',
            count: 1,
          }}
        />
      )}
    </>
  );
};

const List: FC<IListProps> = ({ list, view, type }) => {
  return (
    <>
      <div
        className={clsx(styles.List, {
          [styles.model]: type === 'model',
          [styles.cards]: view === 'cards',
        })}
      >
        {!view || view === 'compact' ? (
          <>
            {type === 'model' ? (
              <div className={styles.table_header}>
                <span>Артикул</span>
                <span>Комм. код</span>
                <span>Название</span>
                <span></span>
              </div>
            ) : (
              <div className={styles.table_header}>
                <span>Тип</span>
                <span>Артикул</span>
                <span>Название</span>
                <span>{type !== 'model' && 'Статус'}</span>
                <span></span>
              </div>
            )}

            {list.map((item, index) => (
              <Item
                key={index}
                id={item.id ?? ''}
                article_number={
                  item.displayData?.partCode || item.displayData?.cmmf || ''
                }
                cmmf={item.displayData?.cmmf ?? ''}
                name={item.displayData?.name ?? ''}
                status={item.displayData?.status ?? ''}
                type={
                  type
                    ? type === 'parts'
                      ? 'part'
                      : type === 'model'
                        ? 'model'
                        : 'accessory'
                    : 'model'
                }
                commercial_number={item.displayData?.commercialCode ?? ''}
                explodedViews={item.displayData?.explodedViews ?? ''}
                repInstruction={item.displayData?.repInstruction ?? ''}
                electricalScheme={item.displayData?.electricalScheme ?? ''}
                techInfo={item.displayData?.techInfo ?? ''}
                replacedBy={item.displayData?.replacedBy ?? []}
                slug={item.displayData.slug}
              />
            ))}
          </>
        ) : (
          list.map((item, index) => (
            <ItemCard
              key={index}
              id={item.id}
              article_number={item.displayData.partCode}
              cmmf={item.displayData.cmmf}
              name={item.displayData.name}
              type={
                type === 'parts'
                  ? 'part'
                  : type === 'model'
                    ? 'model'
                    : 'accessory'
              }
              commercial_number={item.displayData.commercialCode}
              image={item.displayData.image}
              explodedViews={item.displayData.explodedViews}
              repInstruction={item.displayData.repInstruction}
              electricalScheme={item.displayData.electricalScheme}
              techInfo={item.displayData.techInfo}
              slug={item.displayData.slug}
            />
          ))
        )}
      </div>
    </>
  );
};

export default List;
